'use strict';
import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LangContext } from '../App';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import api from '../services/api';

const T = {
  fr: {
    title: 'Soumettre une observation',
    sousTitre:
      "Gagnez 1 crédit par observation publiée. Les validations par pairs peuvent vous rapporter jusqu'à 10 crédits.",
    species: 'Espèce observée',
    especePlaceholder: 'ex. Orignal, Cerf, Bernache…',
    category: 'Catégorie',
    categories: {
      migrateur: 'Espèce migratrice (valide 15 jours)',
      sedentaire: 'Espèce sédentaire (valide 30 jours)',
      spot_peche: 'Spot de pêche (valide 365 jours)',
      etat_zone: 'État de zone (valide 90 jours)',
    },
    typeInfo: "Type d'information",
    typesInfo: {
      passage: 'Passage',
      nidification: 'Nidification',
      hivernage: 'Hivernage',
      presence: 'Présence',
      absence: 'Absence',
      autre: 'Autre',
    },
    nombreEstime: 'Nombre estimé (optionnel)',
    behavior: 'Comportement observé (optionnel)',
    comportementPlaceholder: 'Décrivez le behavior (alimentation, déplacement, repos…)',
    photoUrl: 'URL de la photo',
    photoUrlPlaceholder: 'https://…',
    photoHorodatage: 'Date et heure de la photo (EXIF)',
    photoHorodatageAide:
      "Doit correspondre à l'horodatage EXIF de la photo. Pas dans le futur, pas plus de 24h dans le passé.",
    localisation: 'Localisation',
    localisationAide: 'Cliquez sur «Utiliser ma position» ou entrez les coordonnées manuellement.',
    utiliserPosition: 'Utiliser ma position GPS',
    lat: 'Latitude',
    lng: 'Longitude',
    soumettre: "Soumettre l'observation",
    enCours: 'Soumission en cours…',
    succes: 'Observation soumise ! Vous avez reçu 1 crédit.',
    voirMesCredits: 'Voir mes crédits',
    erreurRequis: 'Veuillez remplir tous les champs obligatoires.',
    erreurGeoloc: "Impossible d'obtenir la position GPS.",
    champRequis: '(requis)',
  },
  en: {
    title: 'Submit an Observation',
    sousTitre:
      'Earn 1 credit per published observation. Peer validations can earn you up to 10 credits.',
    species: 'Observed Species',
    especePlaceholder: 'e.g. Moose, Deer, Canada Goose…',
    category: 'Category',
    categories: {
      migrateur: 'Migratory species (valid 15 days)',
      sedentaire: 'Resident species (valid 30 days)',
      spot_peche: 'Fishing spot (valid 365 days)',
      etat_zone: 'Zone status (valid 90 days)',
    },
    typeInfo: 'Information Type',
    typesInfo: {
      passage: 'Passage',
      nidification: 'Nesting',
      hivernage: 'Wintering',
      presence: 'Presence',
      absence: 'Absence',
      autre: 'Other',
    },
    nombreEstime: 'Estimated count (optional)',
    behavior: 'Observed behaviour (optional)',
    comportementPlaceholder: 'Describe the behaviour (feeding, moving, resting…)',
    photoUrl: 'Photo URL',
    photoUrlPlaceholder: 'https://…',
    photoHorodatage: 'Photo date and time (EXIF)',
    photoHorodatageAide:
      "Must match the photo's EXIF timestamp. Not in the future, not more than 24h in the past.",
    localisation: 'Location',
    localisationAide: 'Click «Use my GPS position» or enter coordinates manually.',
    utiliserPosition: 'Use my GPS position',
    lat: 'Latitude',
    lng: 'Longitude',
    soumettre: 'Submit Observation',
    enCours: 'Submitting…',
    succes: 'Observation submitted! You earned 1 credit.',
    voirMesCredits: 'View my credits',
    erreurRequis: 'Please fill in all required fields.',
    erreurGeoloc: 'Unable to retrieve GPS position.',
    champRequis: '(required)',
  },
};

const styles = {
  page: {
    maxWidth: 880,
    margin: '0 auto',
    paddingBottom: '4rem',
    color: 'var(--couleur-texte)',
  },
  title: {
    fontSize: '1.7rem',
    fontWeight: 700,
    marginBottom: '0.4rem',
  },
  sousTitre: {
    color: 'var(--couleur-texte-attenué)',
    marginBottom: '2rem',
    fontSize: '0.95rem',
  },
  section: {
    background: 'var(--couleur-fond)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 'var(--rayon)',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  label: {
    display: 'block',
    fontWeight: 600,
    marginBottom: '0.4rem',
    fontSize: '0.9rem',
  },
  requis: {
    color: 'var(--couleur-or)',
    fontWeight: 400,
    fontSize: '0.8rem',
    marginLeft: '0.4rem',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.8rem',
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6,
    color: 'var(--couleur-texte)',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '0.6rem 0.8rem',
    background: '#1a2535',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6,
    color: 'var(--couleur-texte)',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '0.6rem 0.8rem',
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6,
    color: 'var(--couleur-texte)',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    minHeight: 90,
    resize: 'vertical',
  },
  aide: {
    fontSize: '0.78rem',
    color: 'var(--couleur-texte-attenué)',
    marginTop: '0.3rem',
  },
  rangee: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
  },
  champ: {
    marginBottom: '1.2rem',
  },
  btnGps: {
    padding: '0.5rem 1rem',
    background: 'rgba(201,162,39,0.15)',
    border: '1px solid var(--couleur-or)',
    borderRadius: 6,
    color: 'var(--couleur-or)',
    cursor: 'pointer',
    fontSize: '0.85rem',
    marginBottom: '0.8rem',
  },
  btnSoumettre: {
    width: '100%',
    padding: '0.85rem',
    background: 'var(--couleur-or)',
    color: '#000',
    border: 'none',
    borderRadius: 8,
    fontWeight: 700,
    fontSize: '1rem',
    cursor: 'pointer',
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  erreur: {
    background: 'rgba(220,38,38,0.12)',
    border: '1px solid rgba(220,38,38,0.4)',
    borderRadius: 8,
    padding: '0.8rem 1rem',
    color: 'var(--couleur-texte-erreur)',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  succes: {
    background: 'rgba(34,197,94,0.12)',
    border: '1px solid rgba(34,197,94,0.4)',
    borderRadius: 8,
    padding: '1.2rem',
    color: 'var(--couleur-texte-succes)',
    textAlign: 'center',
    marginBottom: '1rem',
  },
  lienSucces: {
    display: 'inline-block',
    marginTop: '0.8rem',
    color: 'var(--couleur-or)',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: '0.95rem',
  },
};

function defaultHorodatage() {
  const d = new Date(Date.now() - 5 * 60 * 1000); // 5 min ago
  return d.toISOString().slice(0, 16);
}

export default function SoumettreObservation() {
  const { lang } = useContext(LangContext);
  const navigate = useNavigate();
  const t = T[lang];

  const [species, setEspece] = useState('');
  const [category, setCategorie] = useState('');
  const [typeInfo, setTypeInfo] = useState('');
  const [nombreEstime, setNombreEstime] = useState('');
  const [behavior, setComportement] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [horodatage, setHorodatage] = useState(defaultHorodatage);
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');

  const [enCours, setEnCours] = useState(false);
  const [succes, setSucces] = useState(false);
  const [erreur, setErreur] = useState('');
  const [erreurGps, setErreurGps] = useState('');

  const annule = useRef(false);
  useEffect(
    () => () => {
      annule.current = true;
    },
    []
  );

  function utiliserGps() {
    setErreurGps('');
    if (!navigator.geolocation) {
      setErreurGps(t.erreurGeoloc);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (annule.current) return;
        setLat(String(pos.coords.latitude.toFixed(6)));
        setLng(String(pos.coords.longitude.toFixed(6)));
      },
      () => {
        if (annule.current) return;
        setErreurGps(t.erreurGeoloc);
      }
    );
  }

  async function soumettre(e) {
    e.preventDefault();
    setErreur('');

    if (!species.trim() || !category || !photoUrl.trim() || !horodatage) {
      setErreur(t.erreurRequis);
      return;
    }

    setEnCours(true);
    try {
      const payload = {
        species: species.trim(),
        category,
        photo_url: photoUrl.trim(),
        photo_taken_at: new Date(horodatage).toISOString(),
        ...(typeInfo ? { info_type: typeInfo } : {}),
        ...(nombreEstime ? { estimated_count: parseInt(nombreEstime, 10) } : {}),
        ...(behavior.trim() ? { behavior: behavior.trim() } : {}),
        ...(lat && lng ? { localisation: { lat: parseFloat(lat), lng: parseFloat(lng) } } : {}),
      };
      await api.post('/observations', payload);
      if (!annule.current) setSucces(true);
    } catch (err) {
      if (!annule.current) {
        const msg = err?.response?.data?.error || err?.response?.data?.message || err.message;
        setErreur(msg);
      }
    } finally {
      if (!annule.current) setEnCours(false);
    }
  }

  if (succes) {
    return (
      <PrivateLayout>
        <div style={styles.page}>
          <div style={styles.succes}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
            <strong>{t.succes}</strong>
            <div>
              <span
                style={styles.lienSucces}
                onClick={() => navigate('/my-credits')}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/my-credits')}
              >
                {t.voirMesCredits}
              </span>
            </div>
          </div>
        </div>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout>
      <div style={styles.page}>
        <PageHeader icon="🦌" title={t.title} subtitle={t.sousTitre} />

        <form onSubmit={soumettre} noValidate>
          {/* Observation */}
          <div style={styles.section}>
            <div style={styles.champ}>
              <label style={styles.label}>
                {t.species}
                <span style={styles.requis}>{t.champRequis}</span>
              </label>
              <input
                style={styles.input}
                type="text"
                value={species}
                onChange={(e) => setEspece(e.target.value)}
                placeholder={t.especePlaceholder}
                required
              />
            </div>

            <div style={styles.champ}>
              <label style={styles.label}>
                {t.category}
                <span style={styles.requis}>{t.champRequis}</span>
              </label>
              <select
                style={styles.select}
                value={category}
                onChange={(e) => setCategorie(e.target.value)}
                required
              >
                <option value="">—</option>
                {Object.entries(t.categories).map(([val, label]) => (
                  <option key={val} value={val}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.champ}>
              <label style={styles.label}>{t.typeInfo}</label>
              <select
                style={styles.select}
                value={typeInfo}
                onChange={(e) => setTypeInfo(e.target.value)}
              >
                <option value="">—</option>
                {Object.entries(t.typesInfo).map(([val, label]) => (
                  <option key={val} value={val}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.rangee}>
              <div style={styles.champ}>
                <label style={styles.label}>{t.nombreEstime}</label>
                <input
                  style={styles.input}
                  type="number"
                  min="1"
                  value={nombreEstime}
                  onChange={(e) => setNombreEstime(e.target.value)}
                />
              </div>
            </div>

            <div style={styles.champ}>
              <label style={styles.label}>{t.behavior}</label>
              <textarea
                style={styles.textarea}
                value={behavior}
                onChange={(e) => setComportement(e.target.value)}
                placeholder={t.comportementPlaceholder}
              />
            </div>
          </div>

          {/* Photo */}
          <div style={styles.section}>
            <div style={styles.champ}>
              <label style={styles.label}>
                {t.photoUrl}
                <span style={styles.requis}>{t.champRequis}</span>
              </label>
              <input
                style={styles.input}
                type="url"
                value={photoUrl}
                onChange={(e) => setPhotoUrl(e.target.value)}
                placeholder={t.photoUrlPlaceholder}
                required
              />
            </div>

            <div style={styles.champ}>
              <label style={styles.label}>
                {t.photoHorodatage}
                <span style={styles.requis}>{t.champRequis}</span>
              </label>
              <input
                style={styles.input}
                type="datetime-local"
                value={horodatage}
                onChange={(e) => setHorodatage(e.target.value)}
                required
              />
              <p style={styles.aide}>{t.photoHorodatageAide}</p>
            </div>
          </div>

          {/* Localisation */}
          <div style={styles.section}>
            <p style={{ ...styles.label, marginBottom: '0.8rem' }}>{t.localisation}</p>
            <p style={styles.aide}>{t.localisationAide}</p>
            <button type="button" style={styles.btnGps} onClick={utiliserGps}>
              📍 {t.utiliserPosition}
            </button>
            {erreurGps && (
              <p style={{ color: 'var(--couleur-texte-erreur)', fontSize: '0.82rem' }}>
                {erreurGps}
              </p>
            )}
            <div style={styles.rangee}>
              <div style={styles.champ}>
                <label style={styles.label}>{t.lat}</label>
                <input
                  style={styles.input}
                  type="number"
                  step="any"
                  value={lat}
                  onChange={(e) => setLat(e.target.value)}
                  placeholder="45.5017"
                />
              </div>
              <div style={styles.champ}>
                <label style={styles.label}>{t.lng}</label>
                <input
                  style={styles.input}
                  type="number"
                  step="any"
                  value={lng}
                  onChange={(e) => setLng(e.target.value)}
                  placeholder="-73.5673"
                />
              </div>
            </div>
          </div>

          {erreur && <div style={styles.erreur}>{erreur}</div>}

          <button
            type="submit"
            style={{ ...styles.btnSoumettre, ...(enCours ? styles.btnDisabled : {}) }}
            disabled={enCours}
          >
            {enCours ? t.enCours : t.soumettre}
          </button>
        </form>
      </div>
    </PrivateLayout>
  );
}
